import { computed, watch } from 'vue';
import { useQuery, useSubscription } from '@vue/apollo-composable';

import {
  CountMyUnreadTicketNotificationsDocument,
  TicketNotificationsChangedDocument,
} from '@support/graphql';

import { useAuth } from '@/shared/composables/useAuth';

/**
 * A re-read for a stream of nudges that never answers one with stale data.
 *
 * Apollo de-duplicates an identical request already on the wire, so a refetch
 * issued while the previous one is in flight is answered with THAT one's
 * result — which was read before the change that prompted the second. Two
 * notifications 80ms apart (two replies, a new ticket fanned out to the desk)
 * then leave the badge and the list one behind until the next nudge. Instead,
 * a nudge mid-flight marks the data stale and one more read follows.
 */
export const coalesceRefetch = (refetch: () => Promise<unknown> | undefined) => {
  let running = false;
  let stale = false;

  return async (): Promise<void> => {
    if (running) {
      stale = true;

      return;
    }

    running = true;

    try {
      do {
        stale = false;
        await refetch();
      } while (stale);
    } catch {
      // A failed re-read leaves the previous data on screen; the next nudge or
      // navigation reads again.
    } finally {
      running = false;
    }
  };
};

/**
 * The unread badge, shared by everything that shows it.
 *
 * A composable rather than a store because Apollo already IS the store: the nav
 * rail and the notifications page issue the same query and the second is served
 * from the first's normalized cache. A Pinia store on top would add a copy of
 * that state which can disagree with it.
 *
 * The subscription is what makes the badge worth having. Without it the count
 * is only as fresh as the last navigation — which is precisely the "refresh the
 * inbox with your eyes" problem notifications exist to end.
 */
export const useTicketNotifications = () => {
  const { isGranted } = useAuth();

  // Gated so a role without the grant neither issues a query it would be
  // refused nor renders a badge it can never populate.
  const canSee = computed(() => isGranted('st:core:ticket-notifications:list'));

  const { result, refetch } = useQuery(
    CountMyUnreadTicketNotificationsDocument,
    () => ({ fetchPolicy: 'cache-and-network', enabled: canSee.value }),
  );

  const { result: changed } = useSubscription(
    TicketNotificationsChangedDocument,
    () => ({ enabled: canSee.value }),
  );

  // Re-reading is cheaper to keep correct than patching the cached count by
  // hand; the payload is deliberately just a nudge, like the ticket streams.
  const refetchOnChange = coalesceRefetch(() => refetch());

  watch(changed, () => {
    void refetchOnChange();
  });

  const unreadCount = computed(
    () => result.value?.myUnreadTicketNotificationsCount ?? 0,
  );

  return { canSee, unreadCount, changed, refetchUnreadCount: refetch };
};
